/**
 * @file The quarter processor
 */
import * as tslib_1 from "tslib";
import { Processor } from './processor';
import * as moment from 'moment';
import { Momentos } from '../../base/helpers/momentos';
import { detailLevelDay } from '../helpers/detail-levels';
import { detailLevelWeek } from '../helpers/detail-levels';
import { detailLevelMonth } from '../helpers/detail-levels';
import { TimeslotDecorations } from '../helpers/timeslot-decorations';
var QuarterProcessor = /** @class */ (function (_super) {
    tslib_1.__extends(QuarterProcessor, _super);
    function QuarterProcessor() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /** Duration of first level timeslots, value: weeks. */
        _this.span = 'weeks';
        /** Aantal stappen die gebruikt worden om door de tijdperiode te lopen */
        _this.spanStep = 2;
        return _this;
    }
    /**
     * @param {moment.Moment} startDate
     * @param {moment.Moment} endDate
     * @returns {string}
     */
    QuarterProcessor.prototype.label = function (startDate, endDate) {
        var language = this.displayComponent.localisationService.currentLanguage.formats.agenda.quarter;
        var lastDate = this.lastDayInPeriod(startDate, endDate, this.displayComponent.visibleDays);
        if (lastDate === null) {
            lastDate = endDate;
        }
        var text = startDate.format(language.label);
        var endText = lastDate.format(language.label);
        if (text !== endText) {
            text += ', ' + endText;
        }
        return text;
    };
    /**
     * Creates a description for sub level timeslots.
     * @param startDate Start of timeslot to label.
     * @param endDate End of timeslot to label.
     * @returns A description of the timeslot.
     */
    QuarterProcessor.prototype.details = function (startDate, endDate) {
        var language = this.displayComponent.localisationService.currentLanguage.formats.agenda.quarter;
        var lastDate = this.lastDayInPeriod(startDate, endDate, this.displayComponent.visibleDays);
        if (lastDate === null) {
            lastDate = endDate;
        }
        return startDate.format(language.details) + ' - ' + lastDate.format(language.details);
    };
    /**
     * Generates the bar items for the quarter view
     * @param {GridBarBaseComponent} barComponent
     * @returns {GridItem[]}
     */
    QuarterProcessor.prototype.barGridItems = function (barComponent, gridService) {
        this.displayComponent = barComponent.griddisplay;
        var result = [];
        var start = moment(gridService.startDate).hour(barComponent.visibleHourStart);
        var finish = moment(gridService.endDate).hour(barComponent.visibleHoursEnd);
        var range = new Momentos(start, finish, this.spanStep, this.span, this.endSpan);
        var iteratorResult = range.next();
        while (!iteratorResult.done) {
            var date = iteratorResult.value;
            var endDate = moment(date).add(this.spanStep, this.span).hour(barComponent.visibleHoursEnd);
            var item = {
                label: this.label(date, endDate),
                details: this.details(date, endDate),
                click: {
                    level: detailLevelMonth,
                    date: iteratorResult.value
                }
            };
            if (barComponent.visibleDays.indexOf(date.day()) > -1) {
                result.push(item);
            }
            iteratorResult = range.next();
        }
        return result;
    };
    /**
     * Generates the segments for the quarter view (agenda)
     * @param {AgendaGridDisplayComponent} displayComponent
     * @returns {GridCollections}
     */
    QuarterProcessor.prototype.agendaGridSegments = function (displayComponent, gridService) {
        this.displayComponent = displayComponent;
        var collections = [];
        var language = this.displayComponent.localisationService.currentLanguage.formats.agenda.quarter;
        var start = moment(gridService.startDate).hour(displayComponent.visibleHourStart).startOf('isoWeek');
        var finish = moment(gridService.endDate).endOf('isoWeek').hour(displayComponent.visibleHoursEnd);
        this.clearTimeslots();
        var timeslotDecorations = new TimeslotDecorations('agenda', gridService.level, displayComponent.visibleHourStart, displayComponent.visibleHoursEnd);
        var segments = this.createGridSegments(start.format(language.segments.meta.label) + ' - ' + finish.format(language.segments.meta.label), undefined, undefined, [], timeslotDecorations);
        var range = new Momentos(start, finish, this.spanStep, this.span, this.endSpan);
        var firstDate = null;
        var lastDate = start;
        var iteratorResult = range.next();
        while (!iteratorResult.done) {
            var twoWeekStart = iteratorResult.value;
            var twoWeekEnd = moment(twoWeekStart).add(this.spanStep, this.span);
            var segment = this.createGridSegment('2 week period', [], []);
            var weekRange = new Momentos(twoWeekStart, twoWeekEnd, 1, 'week', 'isoWeek');
            var weekIterator = weekRange.next();
            while (!weekIterator.done) {
                var weekStart = moment(weekIterator.value);
                var weekEnd = moment(weekStart).add(1, 'week');
                var weekSegment = this.createGridRows(weekStart.format(language.segments.segment.meta.label), undefined, undefined, undefined, {
                    level: detailLevelWeek,
                    date: weekStart
                }, []);
                var dayRange = new Momentos(weekStart, weekEnd, 1, 'day', 'day');
                var dayIterator = dayRange.next();
                while (!dayIterator.done) {
                    var offset = moment(dayIterator.value);
                    var dayDate = offset.hour(this.displayComponent.visibleHourStart);
                    var endDayDate = moment(dayDate).hour(this.displayComponent.visibleHoursEnd);
                    var timeslot = this.addTimeslot('AGENDA', dayDate, endDayDate);
                    var day = this.createGridColumn(dayDate.format(language.segments.segment.children.label), offset, endDayDate, this.displayComponent.getResource(), {
                        level: detailLevelDay,
                        date: dayDate
                    }, timeslot);
                    if (displayComponent.visibleDays.indexOf(dayDate.day()) > -1) {
                        weekSegment.children.push(day);
                        lastDate = moment(endDayDate);
                        if (firstDate === null) {
                            firstDate = moment(dayDate);
                        }
                    }
                    dayIterator = dayRange.next();
                }
                if (weekSegment.children.length > 0) {
                    weekSegment.meta = {
                        label: weekStart.format(language.segments.segment.meta.label)
                    };
                    segment.children.push(weekSegment);
                }
                weekIterator = weekRange.next();
            }
            if (segment.children.length > 0) {
                segments.children.push(segment);
            }
            iteratorResult = range.next();
        }
        this.refreshWith('AGENDA', displayComponent.objects.items);
        this.queryStartDate = firstDate ? firstDate.clone() : null;
        this.queryFinishDate = lastDate ? lastDate.clone() : null;
        collections.push(segments);
        return collections;
    };
    /**
     * Generates the segments for the quarter view (planboard)
     * @param {PlanboardGridDisplayComponent} displayComponent
     * @returns {GridCollections}
     */
    QuarterProcessor.prototype.planboardGridSegments = function (displayComponent, gridService) {
        var _this = this;
        this.displayComponent = displayComponent;
        var collections = [];
        var language = this.displayComponent.localisationService.currentLanguage.formats.agenda.quarter;
        var start = moment(gridService.startDate).hour(displayComponent.visibleHourStart).startOf('isoWeek');
        var finish = moment(gridService.endDate).endOf('isoWeek').hour(displayComponent.visibleHoursEnd);
        var timeslotDecorations = new TimeslotDecorations('planboard', gridService.level, displayComponent.visibleHourStart, displayComponent.visibleHoursEnd);
        this.clearTimeslots();
        var firstDate = null;
        var lastDate = start;
        displayComponent.resources.items.forEach(function (resource) {
            var segments = _this.createGridSegments(start.format(language.segments.meta.label) + ' - ' + finish.format(language.segments.meta.label), resource, undefined, [], timeslotDecorations);
            var range = new Momentos(start, finish, _this.spanStep, _this.span, _this.endSpan);
            var iteratorResult = range.next();
            while (!iteratorResult.done) {
                var twoWeekStart = iteratorResult.value;
                var twoWeekEnd = moment(twoWeekStart).add(_this.spanStep, _this.span);
                var segment = _this.createGridSegment('2 week period', [], []);
                var weekRange = new Momentos(twoWeekStart, twoWeekEnd, 1, 'week', 'isoWeek');
                var weekIterator = weekRange.next();
                while (!weekIterator.done) {
                    var weekStart = moment(weekIterator.value);
                    var weekEnd = moment(weekStart).add(1, 'week');
                    var weekSegment = _this.createGridRows(weekStart.format(language.segments.segment.meta.label), undefined, undefined, undefined, {
                        level: detailLevelWeek,
                        date: weekStart
                    }, []);
                    var dayRange = new Momentos(weekStart, weekEnd, 1, 'day', 'day');
                    var dayIterator = dayRange.next();
                    while (!dayIterator.done) {
                        var offset = moment(dayIterator.value);
                        var dayDate = offset.hour(_this.displayComponent.visibleHourStart);
                        var endDayDate = moment(dayDate).hour(_this.displayComponent.visibleHoursEnd);
                        var timeslot = _this.addTimeslot(resource['Oid'], dayDate, endDayDate);
                        var day = _this.createGridColumn(dayDate.format(language.segments.segment.children.label), offset, endDayDate, resource, {
                            level: detailLevelDay,
                            date: dayDate
                        }, timeslot);
                        if (displayComponent.visibleDays.indexOf(dayDate.day()) > -1) {
                            weekSegment.children.push(day);
                            lastDate = moment(endDayDate);
                            if (firstDate === null) {
                                firstDate = moment(dayDate);
                            }
                        }
                        dayIterator = dayRange.next();
                    }
                    if (weekSegment.children.length > 0) {
                        segment.children.push(weekSegment);
                    }
                    weekIterator = weekRange.next();
                }
                if (segment.children.length > 0) {
                    segments.children.push(segment);
                }
                iteratorResult = range.next();
            }
            collections.push(segments);
        });
        this.queryStartDate = firstDate ? firstDate.clone() : null;
        this.queryFinishDate = lastDate ? lastDate.clone() : null;
        return collections;
    };
    /**
     * Generates the segments for the quarter view (plugin)
     * @param {PluginViewBase} pluginView
     * @returns {GridCollections}
     */
    QuarterProcessor.prototype.pluginGridSegments = function (pluginView, gridService) {
        var collections = [];
        var language = pluginView.localisationService.currentLanguage.formats.agenda.quarter;
        var start = moment(gridService.startDate).hour(pluginView.visibleHourStart).startOf('isoWeek');
        var finish = moment(gridService.endDate).endOf('isoWeek').hour(pluginView.visibleHoursEnd);
        var timeslotDecorations = new TimeslotDecorations('planboard', gridService.level, pluginView.visibleHourStart, pluginView.visibleHoursEnd);
        var firstDate = null;
        var lastDate = start;
        this.keyedTimeslots.Remove(pluginView.pluginKey);
        var segments = this.createGridSegments(start.format(language.segments.meta.label) + ' - ' + finish.format(language.segments.meta.label), {}, undefined, [], timeslotDecorations);
        var range = new Momentos(start, finish, this.spanStep, this.span, this.endSpan);
        var iteratorResult = range.next();
        while (!iteratorResult.done) {
            var twoWeekStart = iteratorResult.value;
            var twoWeekEnd = moment(twoWeekStart).add(this.spanStep, this.span);
            var segment = this.createGridSegment('2 week period', [], []);
            var weekRange = new Momentos(twoWeekStart, twoWeekEnd, 1, 'week', 'isoWeek');
            var weekIterator = weekRange.next();
            while (!weekIterator.done) {
                var weekStart = moment(weekIterator.value);
                var weekEnd = moment(weekStart).add(1, 'week');
                var weekSegment = this.createGridRows(weekStart.format(language.segments.segment.meta.label), undefined, undefined, undefined, {
                    level: detailLevelWeek,
                    date: weekStart
                }, []);
                var dayRange = new Momentos(weekStart, weekEnd, 1, 'day', 'day');
                var dayIterator = dayRange.next();
                while (!dayIterator.done) {
                    var offset = moment(dayIterator.value);
                    var dayDate = offset.hour(pluginView.visibleHourStart);
                    var endDayDate = moment(dayDate).hour(pluginView.visibleHoursEnd);
                    var timeslot = this.addTimeslot(pluginView.pluginKey, dayDate, endDayDate);
                    var day = this.createGridColumn(dayDate.format(language.segments.segment.children.label), offset, endDayDate, {}, {
                        level: detailLevelDay,
                        date: dayDate
                    }, timeslot);
                    if (pluginView.visibleDays.indexOf(dayDate.day()) > -1) {
                        weekSegment.children.push(day);
                        lastDate = moment(endDayDate);
                        if (firstDate === null) {
                            firstDate = moment(dayDate);
                        }
                    }
                    dayIterator = dayRange.next();
                }
                if (weekSegment.children.length > 0) {
                    segment.children.push(weekSegment);
                }
                weekIterator = weekRange.next();
            }
            if (segment.children.length > 0) {
                segments.children.push(segment);
            }
            iteratorResult = range.next();
        }
        collections.push(segments);
        this.queryStartDate = firstDate ? firstDate.clone() : null;
        this.queryFinishDate = lastDate ? lastDate.clone() : null;
        return collections;
    };
    return QuarterProcessor;
}(Processor));
export { QuarterProcessor };
